import { useEffect } from "react";

interface UseAutoScrollToElementOptions {
  /**
   * Delay in milliseconds before scrolling (to wait for DOM/animations to settle)
   * @default 100
   */
  delay?: number;
  /**
   * Scroll behavior
   * @default "smooth"
   */
  behavior?: ScrollBehavior;
  /**
   * Optional container ref - if provided, scrolls within the container instead of the page
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Hook to automatically scroll to an element when dependencies change
 * Used for auto-scrolling to selected/highlighted items in dropdowns, lists, etc.
 *
 * @param elementRef - Ref to the element to scroll to
 * @param enabled - Whether auto-scroll is enabled
 * @param deps - Dependencies that trigger the scroll
 * @param options - Scroll options
 *
 * @example
 * ```tsx
 * const selectedRef = useRef<HTMLDivElement>(null);
 * const containerRef = useRef<HTMLDivElement>(null);
 *
 * useAutoScrollToElement(
 *   selectedRef,
 *   hasSelectedItem,
 *   [items, selectedId],
 *   { containerRef }
 * );
 *
 * return (
 *   <div ref={containerRef}>
 *     <div ref={isSelected ? selectedRef : null}>Item</div>
 *   </div>
 * );
 * ```
 */
export const useAutoScrollToElement = (
  elementRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  deps: React.DependencyList,
  options: UseAutoScrollToElementOptions = {},
) => {
  const { delay = 100, behavior = "smooth", containerRef } = options;

  useEffect(() => {
    if (!enabled) return;

    // Use setTimeout to wait for DOM to settle
    const timeoutId = setTimeout(() => {
      const element = elementRef.current;

      if (!element) return;

      // If container ref is provided, scroll within the container
      if (containerRef?.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const containerHeight = container.clientHeight || containerRect.height;

        // Calculate scroll position to center the element in the container
        // offsetTop is relative to the container's content area, not the viewport
        const centeredTop = element.offsetTop - container.offsetTop - containerHeight / 2 + elementRect.height / 2;
        const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
        const clampedTop = Math.min(Math.max(centeredTop, 0), maxScrollTop);

        // Check if we're already at the correct scroll position (within a small threshold)
        const currentScrollTop = container.scrollTop;
        const scrollDifference = Math.abs(currentScrollTop - clampedTop);
        const threshold = 10; // pixels

        // Only scroll if we're not already at the right position
        if (scrollDifference > threshold) {
          container.scrollTo({
            top: clampedTop,
            behavior,
          });
        }
      } else {
        // Fallback to scrollIntoView for page-level scrolling (e.g., dropdowns)
        element.scrollIntoView({
          block: "center",
          inline: "center",
          behavior,
        });
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, deps);
};
